import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardFilter, Period, Phase } from '../../services/types/Dashboard';
import { ButtonBar, DropDown } from '../../shared/components';
import MainContext from '../App/MainProvider/context';
import Role from '../../types/role';
import MembersDropDown from './MembersDropDown';
import RoundButton from '../../shared/components/RoundButton';
import { findMapped, findMapId } from '../../shared/misc/utils';

interface Props {
  filter: DashboardFilter;
  send: (type: string, rest?: Record<string, unknown>) => void;
  periodText: string | null;
}

const Bar: React.FC<Props> = ({ filter, send, periodText }) => {
  const { t } = useTranslation();
  const { user } = useContext(MainContext);

  const modes = [
    { id: 1, text: t('gantt'), htmlId: 'mGantt' },
    { id: 2, text: t('list'), htmlId: 'mList' },
  ];

  const periods = [
    { id: 1, text: t('week'), htmlId: 'pWeek', mapped: Period.Week },
    { id: 2, text: t('twoWeeks'), htmlId: 'pTwoWeeks', mapped: Period.TwoWeeks },
    { id: 3, text: t('month'), htmlId: 'pMonth', mapped: Period.Month },
  ];

  const phases = [
    { id: 0, text: t('all'), htmlId: 'phAll', mapped: null },
    { id: 1, text: t('identification'), htmlId: 'phIdentification', mapped: Phase.Identification },
    { id: 2, text: t('structuring'), htmlId: 'phStructuring', mapped: Phase.Structuring },
    { id: 3, text: t('approval'), htmlId: 'phApproval', mapped: Phase.Approval },
    { id: 4, text: t('execution'), htmlId: 'phExecution', mapped: Phase.Execution },
  ];

  const setMode = (modeId: number) => send('GANTT_MODE', { value: modeId === 1 });

  const setPeriod = (periodId: number) => {
    const period = findMapped(periods, periodId);
    send('PERIOD', { value: period });
  };

  const setPhase = (phaseId: number) => {
    const phase = findMapped(phases, phaseId);
    send('PHASE', { value: phase });
  };

  const setRel = (increment: boolean) => send('PERIOD_RELATIVE', { increment });

  return (
    <div className="button-container-bar">
      <div className="scrollablex-wrapper">
        <ButtonBar
          id="modes"
          value={filter.ganttMode ? 1 : 2}
          onChange={setMode}
          elements={modes}
        />
        {!filter.ganttMode ? null : (
          <>
            <div className="mr-3" />
            <ButtonBar
              id="periods"
              value={findMapId(periods, filter.period)}
              onChange={setPeriod}
              elements={periods}
            />
          </>
        )}
        <div className="mr-3" />
        <h5 className="m-1">{t('phase')}</h5>
        <DropDown elements={phases} value={findMapId(phases, filter.phase)} onChange={setPhase} />
        {user.roleCode === Role.GroupHead && (
          <MembersDropDown memberId={filter.memberUserId} send={send} />
        )}
      </div>
      <div className="button-container-bar--right">
        {!filter.ganttMode ? null : (
          <>
            <h5 className="m-1">{periodText || 'Prev - Next'}</h5>
            <RoundButton className="secondary" onClick={() => setRel(false)} icon="chevron-left" />
            <RoundButton className="secondary" onClick={() => setRel(true)} icon="chevron-right" />
          </>
        )}
      </div>
    </div>
  );
};

export default Bar;
